import React, { useMemo, useCallback } from "react";
import css from "./EffectivenessCard.module.scss";
import {
  Flex,
  Loader,
  Rating,
  Text,
  Title,
  Divider,
  Alert,
} from "@mantine/core";
import ReactECharts from "echarts-for-react";
import {
  FiInfo,
  FiAlertTriangle,
  FiCheckCircle,
  FiAlertCircle,
  FiEyeOff,
  FiLock,
} from "react-icons/fi";
import Tooltip from "components/Tooltip/Tooltip";
import useInsightsDrilldown from "hooks/useInsightsDrilldown";
import {
  getEffectivenessRatingMessage,
  getEffectivenessRatingMessageForAttendee,
} from "utils/insights";
import { formatNumber } from "utils/common";
import { IconAdjustments } from "@tabler/icons-react";

const EffectivenessCard = ({
  type,
  isLoading,
  isError,
  data,
  label = "Effectiveness",
  uid,
  duration,
  self,
}) => {
  const { openDrilldown } = useInsightsDrilldown();

  // "3 star" becomes the "3star" segment of the redis key.
  const buildRatingRedisKey = useCallback(
    (rating) => {
      if (!uid || !duration || !self || !rating) return null;
      const collection = type === "org" ? "org" : "user";
      const [count, unit] = rating.split(" ");
      return `users:${uid}:${collection}Calculations:effectiveness:${duration}:${self}:${count}${unit}`;
    },
    [uid, duration, self, type]
  );

  const chartData = useMemo(() => {
    if (!isLoading && data && !data.message) {
      return [
        data?.ratings?.["1 star"].count,
        data?.ratings?.["2 star"].count,
        data?.ratings?.["3 star"].count,
        data?.ratings?.["4 star"].count,
        data?.ratings?.["5 star"].count,
      ];
    }
    return [];
  }, [data, isLoading]);

  const percentageData = useMemo(() => {
    if (!isLoading && data && !data.message) {
      return [
        data?.ratings?.["1 star"]?.percentage,
        data?.ratings?.["2 star"]?.percentage,
        data?.ratings?.["3 star"]?.percentage,
        data?.ratings?.["4 star"]?.percentage,
        data?.ratings?.["5 star"]?.percentage,
      ];
    }
    return [];
  }, [data, isLoading]);

  const chartOption = useMemo(
    () => ({
      xAxis: {
        max: "dataMax",
        axisTick: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        splitLine: {
          show: false, // Hide the lines between the ticks
        },
        axisLabel: {
          show: false, // Hide the tick labels
        },
      },
      yAxis: [
        {
          type: "category",
          data: ["1 star", "2 star", "3 star", "4 star", "5 star"],
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        {
          type: "category",
          data: percentageData?.map(
            (value) => `${parseFloat(value).toFixed(1)}%`
          ),
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
      ],
      series: [
        {
          type: "bar",
          data: chartData,
          label: {
            show: false,
            position: "right",
          },
          barWidth: 10,
          itemStyle: {
            normal: {
              color: "#FFC107",
              borderRadius: [20, 20, 20, 20],
            },
          },
        },
      ],
      legend: {
        show: false,
      },
      grid: {
        height: "120px",
        right: "15%",
        bottom: "25%",
        left: "15%",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
    }),
    [chartData, percentageData]
  );

  const handleBarClick = useCallback(
    (params) => {
      const { dataIndex } = params;
      const starRatings = ["1 star", "2 star", "3 star", "4 star", "5 star"];
      const selectedRating = starRatings[dataIndex];

      const ratingData = data?.ratings?.[selectedRating];

      if (ratingData) {
        openDrilldown({
          name: `${selectedRating} meetings`,
          type: "Effectiveness Rating",
          meetingType: "Rated",
          redisKey: buildRatingRedisKey(selectedRating),
        });
      }
    },
    [data, openDrilldown, buildRatingRedisKey]
  );

  const onEvents = useMemo(
    () => ({
      click: handleBarClick,
    }),
    [handleBarClick]
  );

  const totalRatings = Object.values(data?.ratings || {}).reduce(
    (acc, curr) => acc + curr.count,
    0
  );

  if (isLoading) {
    return (
      <EffectivenessCardWrapper type={type} data={data} label={label}>
        <div className="loader">
          <Loader />
          <span>Fetching Effectiveness score</span>
        </div>
      </EffectivenessCardWrapper>
    );
  }

  if (isError) {
    return (
      <EffectivenessCardWrapper type={type} data={data} label={label}>
        <div className="loader">
          <span>Error while fetching data</span>
        </div>
      </EffectivenessCardWrapper>
    );
  }

  if (data?.message) {
    return (
      <EffectivenessCardWrapper type={type} data={data} label={label}>
        <div className="loader">
          <span>Have no meetings</span>
        </div>
      </EffectivenessCardWrapper>
    );
  }

  const effectivenessAverageRating = parseFloat(
    data?.totalAverageOfRatings
  ).toFixed(1);
  const renderedEffectivenessAverageRating = isNaN(effectivenessAverageRating)
    ? "-"
    : effectivenessAverageRating;

  if (data?.visibility === "hidden") {
    return (
      <EffectivenessCardWrapper type={type} data={data} label={label}>
        <Flex direction="column" justify={"space-between"} h={"100%"}>
          <div className={css.hiddenState}>
            <div className={css.hiddenIcon}>
              <FiEyeOff size={32} />
            </div>
            <Text size="sm" color="dimmed" ta="center" fw={500}>
              Data is hidden
            </Text>
            <Text size="xs" color="dimmed" ta="center" mt={4}>
              This information is not available for viewing
            </Text>
          </div>

          <Flex direction="column">
            <Divider my={10} />
            <Alert
              icon={<FiLock size={16} />}
              color="blue"
              variant="light"
              size="sm"
            >
              You had too few meetings in selected period. To ensure data quality and platform
              anonymity, we hide the analytics of your selected period ratings.
            </Alert>
          </Flex>
        </Flex>
      </EffectivenessCardWrapper>
    );
  }

  if (data?.visibility === "partial") {
    return (
      <EffectivenessCardWrapper type={type} data={data} label={label}>
        <Flex direction="column" justify="space-between" h="100%">
          <div className={css.partialState}>
            <div className={css.partialRating}>
              <Title order={1}>{renderedEffectivenessAverageRating}</Title>
              <Rating
                value={renderedEffectivenessAverageRating}
                fractions={2}
                readOnly
                size="lg"
              />
              <Text fz="sm" color="dimmed" ta="center" fw={500}>
                Average Rating
              </Text>
            </div>
          </div>

          <Flex direction="column">
            <Divider my={10} />
            <Alert
              icon={<FiInfo size={16} />}
              color="orange"
              variant="light"
              size="sm"
            >
              Number of ratings are too few in selected period to show the
              breakdown. We hide the breakdown to ensure anonymity and data
              quality.
            </Alert>
          </Flex>
        </Flex>
      </EffectivenessCardWrapper>
    );
  }

  return (
    <EffectivenessCardWrapper type={type} data={data} label={label}>
      <div className={css.chart}>
        <ReactECharts option={chartOption} onEvents={onEvents} />
      </div>

      <div className={css.detail}>
        <div>
          <Title order={1}>{renderedEffectivenessAverageRating}</Title>
          <div>
            <Rating
              value={renderedEffectivenessAverageRating}
              fractions={2}
              readOnly
            />
            <Text fz="sm">Average Rating</Text>
          </div>
        </div>
        <div>
          <Text fz={"md"}>
            <Text fz={"lg"} display={"inline"} fw={"bold"} color="grey">
              {formatNumber(
                Object.values(data?.ratings || {}).reduce(
                  (acc, curr) => acc + curr?.count,
                  0
                ),
                0
              )}
            </Text>{" "}
            {["org", "organizer", "all"].includes(type)
              ? `ratings / ${formatNumber(data?.totalAttendees, 0)} attendees`
              : `ratings / ${formatNumber(
                  data?.totalMeetingsAttendeedNotAsOrganizer,
                  0
                )} meetings`}
          </Text>
        </div>
      </div>

      {totalRatings > 0 &&
        (type === "attendee"
          ? Number(data.totalMeetingsAttendeedNotAsOrganizer) > 0
          : Number(data.totalAttendees) > 0) && (
          <Flex direction="column">
            <Divider my={10} />
            {(() => {
              const messageData =
                type === "attendee"
                  ? getEffectivenessRatingMessageForAttendee(
                      totalRatings,
                      data?.totalMeetingsAttendeedNotAsOrganizer
                    )
                  : getEffectivenessRatingMessage(
                      totalRatings,
                      data?.totalAttendees
                    );

              const getIcon = (type) => {
                switch (type) {
                  case "critical":
                    return <FiAlertCircle size={16} />;
                  case "warning":
                    return <FiAlertTriangle size={16} />;
                  case "moderate":
                    return <FiInfo size={16} />;
                  case "good":
                    return <FiCheckCircle size={16} />;
                  default:
                    return <FiInfo size={16} />;
                }
              };

              if (messageData) {
                return (
                  <Alert
                    icon={getIcon(messageData.type)}
                    color={messageData.color}
                    variant="light"
                    size="sm"
                    h={90}
                  >
                    {messageData.message}
                  </Alert>
                );
              }
            })()}
          </Flex>
        )}
    </EffectivenessCardWrapper>
  );
};

export default EffectivenessCard;

const EffectivenessCardWrapper = ({ type, children, label }) => {
  return (
    <div className={`card ${css.container}`}>
      <Flex justify={"space-between"} align={"center"} style={{ zIndex: 1 }}>
        <Flex align={"center"}>
          <IconAdjustments size={18} style={{ marginRight: 8 }} />
          <Title order={5}>
            {type === "organizer"
              ? "Effectiveness of organized"
              : type === "attendee"
              ? "You rated"
              : label}
          </Title>
        </Flex>
        <Tooltip label="Explanation of how it works">
          <div>
            <FiInfo size={16} color="grey" />
          </div>
        </Tooltip>
      </Flex>
      {children}
    </div>
  );
};
